import { ArticleFacets, Search, SearchFilters, SearchFiltersTypeTypeEnum } from '@api/api';
import { buildSearchPageLink } from '@utils/link';
import { Translate } from 'next-translate';

export interface ActiveTabInfo {
  name: string;
  type?: SearchFiltersTypeTypeEnum;
  filterValue?: string;
}

export const getActiveTabInfo = (
  { subcategories, tags, categories }: SearchFilters,
  tab: string,
  translate: Translate,
): ActiveTabInfo => {
  if (tab === 'collections') {
    return { name: translate(tab) };
  }

  if (tab !== 'all') {
    const activeTab =
      categories.find((category) => category.slug === tab) ??
      subcategories.find((subcategory) => subcategory.slug === tab) ??
      tags.find((tag) => tag.name === tab);

    return {
      name: activeTab.name,
      filterValue:
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        activeTab.type === SearchFiltersTypeTypeEnum.Tag ? activeTab.name : activeTab.slug,
      type: activeTab.type,
    };
  }

  return { name: translate('all') };
};

const buildSearchFiltersTab = (
  name: string,
  query: string,
  resultCount = 0,
  activeTabInfo?: ActiveTabInfo,
  tab?: string,
) =>
  resultCount || activeTabInfo?.name === name
    ? {
        id: name,
        name: `${name} (${resultCount})`,
        href: buildSearchPageLink(String(query ?? ''), tab),
      }
    : null;

export const getTabs = (
  { tags, subcategories, categories }: SearchFilters,
  resultCount: SearchResultCount,
  query: string,
  t,
  activeTabInfo: ActiveTabInfo,
) =>
  [
    buildSearchFiltersTab(t('all'), query, resultCount.articleAll, activeTabInfo),
    ...categories.map(({ name, slug }) =>
      buildSearchFiltersTab(name, query, resultCount.categoriesSlug?.[slug], activeTabInfo, slug),
    ),
    ...subcategories.map(({ name, slug }) =>
      buildSearchFiltersTab(
        name,
        query,
        resultCount.subcategoriesSlug?.[slug],
        activeTabInfo,
        slug,
      ),
    ),
    ...tags.map(({ name }) =>
      buildSearchFiltersTab(name, query, resultCount.tagsName?.[name], activeTabInfo, name),
    ),
    buildSearchFiltersTab(
      t('collections'),
      query,
      resultCount.collection,
      activeTabInfo,
      'collections',
    ),
  ].filter(Boolean);

const filterByType = {
  [SearchFiltersTypeTypeEnum.Tag]: 'tags.name',
  [SearchFiltersTypeTypeEnum.Category]: 'categories.slug',
  [SearchFiltersTypeTypeEnum.Subcategory]: 'subcategories.slug',
};

export const buildSearchFilter = (type: SearchFiltersTypeTypeEnum, value: string): string => {
  return `${filterByType[type]}:${value}`;
};

export interface SearchResultCount extends ArticleFacets {
  articleAll?: number;
  collection?: number;
}

export const getResultCount = (searchData: { data: Search }[]): SearchResultCount => {
  const [resultCount = {}] = searchData.map(({ data: { searchResults } }) => {
    return {
      ...searchResults.article.facets,
      articleAll: searchResults.article.nbHits,
      collection: searchResults.collection.nbHits,
    };
  });

  return resultCount;
};
